Vue.component('RewardsComponent', {
    template: templates.RewardsComponent,
    props: {
        rewards: {
            type: Object,
            required: false,
            default: () => null
        },
        packs: {
            type: Object,
            required: true,
            default: () => ({})
        },
        randomcard: {
            type: Array,
            required: true,
            default: () => []
        },
        selectedCards: {
            type: [Array, Object],
            required: false,
            default: () => ({})
        },
        eventCards: {
            type: Array,
            required: false,
            default: () => []
        },
        frames: {
            type: Object,
            required: false,
            default: () => ({})
        },
        backgrounds: {
            type: Object,
            required: false,
            default: () => ({})
        },
        cardBacks: {
            type: Object,
            required: false,
            default: () => ({})
        },
        chests: {
            type: Object,
            required: false,
            default: () => ({})
        },
        ownedShopItems: {
            type: Array,
            required: false,
            default: () => []
        },
        allowChests: {
            type: Boolean,
            required: false,
            default: false
        },
        allowShopItems: {
            type: Boolean,
            required: false,
            default: false
        },
        exoticCards: {
            type: Object,
            required: false,
            default: () => ({})
        }
    },

    data() {
        return {
            availableDeltaCards: []
        };
    },

    mounted() {
        const c = this;
        if (typeof deltaCardService !== 'undefined') {
            deltaCardService.get()
                .then(function(response) {
                    c.availableDeltaCards = response.data.filter(d => d.active);
                })
                .catch(function() {});
        }
    },

    computed: {
        // Safe rewards object with fallback
        safeRewards() {
            return this.rewards || {
                barks: 0,
                xp: 0,
                givePack: [{ packId: null, packs_quantity: 0 }],
                randomCard: [{ randomCard: 0, random_amount: 0 }],
                selectedCard: [{ cardId: null, card_amount: 0, card_tier: 0 }],
                eventCard: [{ id: null, card_amount: 0, card_tier: 0 }],
                variantCard: [{ cardId: null, card_amount: 0, card_tier: 9 }],
                deltaCard: [{ deltaCardId: null, card_amount: 0 }],
                giveChest: [{ chestId: null, chest_quantity: 0 }],
                giveShopItem: [{ itemId: null, itemType: null }],
                exoticCard: [{ cardId: null }],
                rewardFrames: [],
                rewardFrameMode: 'single-frame',
                rewardCardBacks: [],
                packs_amount: 0,
                random_amount: 0,
                cards_amount: 0,
                event_cards_amount: 0,
                variant_cards_amount: 0,
                delta_cards_amount: 0,
                chests_amount: 0,
                shop_items_amount: 0,
            };
        },
        
        // Safe access to reward arrays with fallbacks
        rewardPacks() {
            return this.safeRewards.givePack || [];
        },
        
        rewardRandomCards() {
            return this.safeRewards.randomCard || [];
        },
        
        rewardSelectedCards() {
            return this.safeRewards.selectedCard || [];
        },
        
        rewardEventCards() {
            return this.safeRewards.eventCard || [];
        },

        rewardExoticCards() {
            return this.safeRewards.exoticCard || [];
        },

        rewardVariantCards() {
            return this.safeRewards.variantCard || [];
        },

        rewardDeltaCards() {
            return this.safeRewards.deltaCard || [];
        },

        hasExoticCard() {
            return Object.keys(this.exoticCards).length > 0;
        },

        variantCards() {
            return Object.values(this.selectedCards).filter(card => card.variant === true);
        },

        deltaCards() {
            return this.availableDeltaCards;
        },

        hasVariantCard() {
            return this.variantCards.length > 0;
        },

        hasDeltaCard() {
            return this.availableDeltaCards.length > 0;
        },

        rewardChests() {
            return this.safeRewards.giveChest || [];
        },

        rewardShopItems() {
            return this.safeRewards.giveShopItem || [];
        },
        
        // Counter properties - now using safeRewards
        packsAmount() {
            return this.safeRewards.packs_amount || 0;
        },
        
        randomAmount() {
            return this.safeRewards.random_amount || 0;
        },
        
        cardAmount() {
            return this.safeRewards.cards_amount || 0;
        },

        eventCardAmount() {
            return this.safeRewards.event_cards_amount || 0;
        },

        variantCardAmount() {
            return this.safeRewards.variant_cards_amount || 0;
        },

        deltaCardAmount() {
            return this.safeRewards.delta_cards_amount || 0;
        },

        chestsAmount() {
            return this.safeRewards.chests_amount || 0;
        },

        shopItemsAmount() {
            return this.safeRewards.shop_items_amount || 0;
        },
        
        // Additional items (excluding first item for display purposes)
        additionalPacks() {
            return this.rewardPacks.slice(1);
        },
        
        additionalRandomCards() {
            return this.rewardRandomCards.slice(1);
        },
        
        additionalSelectedCards() {
            return this.rewardSelectedCards.slice(1);
        },

        additionalEventCards() {
            return this.rewardEventCards.slice(1);
        },

        additionalVariantCards() {
            return this.rewardVariantCards.slice(1);
        },

        additionalDeltaCards() {
            return this.rewardDeltaCards.slice(1);
        },

        additionalChests() {
            return this.rewardChests.slice(1);
        },

        additionalShopItems() {
            return this.rewardShopItems.slice(1);
        },
        
        // Boolean helpers for reward existence - now using safeRewards
        hasBarks() {
            return this.safeRewards?.barks !== undefined;
        },
        
        hasXp() {
            return this.safeRewards?.xp !== undefined;
        },
        
        hasGivePack() {
            return this.safeRewards?.givePack !== undefined;
        },
        
        hasRandomCard() {
            return this.safeRewards?.randomCard !== undefined;
        },
        
        hasSelectedCard() {
            return this.safeRewards?.selectedCard !== undefined;
        },

        hasEventCard() {
            return this.safeRewards?.eventCard !== undefined;
        },

        hasGiveChest() {
            return this.allowChests && this.safeRewards?.giveChest !== undefined;
        },

        hasGiveShopItem() {
            return this.allowShopItems && this.safeRewards?.giveShopItem !== undefined;
        },

        // Reward Frames/Backgrounds
        hasRewardFrames() {
            return this.safeRewards?.rewardFrames !== undefined;
        },

        rewardFrameMode() {
            return this.safeRewards.rewardFrameMode || 'single-frame';
        },

        selectedRewardItems() {
            return this.safeRewards.rewardFrames || [];
        },

        // Filter available items based on selected mode
        availableRewardItems() {
            const mode = this.rewardFrameMode;
            let available = [];

            if (mode === 'single-frame') {
                // Show only frames
                available = Object.values(this.frames).map(frame => ({
                    ...frame,
                    type: 'frame',
                    displayName: frame.name
                }));
            } else if (mode === 'single-background') {
                // Show only backgrounds
                available = Object.values(this.backgrounds).map(bg => ({
                    ...bg,
                    type: 'background',
                    displayName: bg.name
                }));
            } else if (mode === 'multiple') {
                // Combine both frames and backgrounds
                const frames = Object.values(this.frames).map(frame => ({
                    ...frame,
                    type: 'frame',
                    displayName: `${frame.name} [Frame]`
                }));

                const backgrounds = Object.values(this.backgrounds).map(bg => ({
                    ...bg,
                    type: 'background',
                    displayName: `${bg.name} [Background]`
                }));

                available = [...frames, ...backgrounds];
            }

            return available;
        },

        rewardFramesSelectedIds: {
            get() {
                return this.selectedRewardItems.map(item => item.id);
            },
            set(selectedIds) {
                this.updateRewardFramesSelection(selectedIds);
            }
        },

        cardBacksSelectedIds: {
            get() {
                return this.selectedCardBacks;
            },
            set(selectedIds) {
                this.updateCardBacksSelection(selectedIds);
            }
        },

        // Get full item objects for selected items (for preview)
        selectedRewardItemsPreview() {
            return this.selectedRewardItems.map(item => {
                const collection = item.type === 'background' ? this.backgrounds : this.frames;
                const fullItem = collection[item.id];
                if (!fullItem) return null;

                return {
                    ...fullItem,
                    type: item.type
                };
            }).filter(Boolean);
        },

        // Card Backs
        hasRewardCardBacks() {
            return Object.keys(this.cardBacks).length > 0;
        },

        selectedCardBacks() {
            return (this.rewards && this.rewards.rewardCardBacks) || [];
        },

        selectedCardBacksPreview() {
            return this.selectedCardBacks.map(id => {
                const back = this.cardBacks[id];
                return back ? { ...back, id } : null;
            }).filter(Boolean);
        }
    },
    methods: {
        // Generic method to update simple reward fields
        updateField(field, value) {
            const parsedValue = this.parseValue(value, field);
            const updatedRewards = { ...this.safeRewards, [field]: parsedValue };
            this.emitRewardChange(updatedRewards);
        },
        
        // Helper to parse values based on field type
        parseValue(value, field) {
            const numericFields = ['barks', 'xp', 'packs_amount', 'random_amount', 'cards_amount', 'card_tier', 'event_cards_amount'];
            return numericFields.includes(field) ? (parseInt(value) || 0) : value;
        },
        
        // Centralized emit method
        emitRewardChange(updatedRewards) {
            this.$emit('reward-changed', updatedRewards);
        },
        
        // Generic safe getter for array items
        getArrayItemValue(array, index, field, defaultValue = '') {
            if (array && array[index]?.hasOwnProperty(field)) {
                return array[index][field];
            }
            return typeof defaultValue === 'string' ? defaultValue : 0;
        },
        
        // Specific getters using the generic method
        getPackValue(index, field) {
            const defaultValue = field === 'packId' ? '' : 0;
            return this.getArrayItemValue(this.rewardPacks, index, field, defaultValue);
        },
        
        getRandomCardValue(index, field) {
            const defaultValue = field === 'randomCard' ? '' : 0;
            return this.getArrayItemValue(this.rewardRandomCards, index, field, defaultValue);
        },
        
        getSelectedCardValue(index, field) {
            const defaultValue = field === 'cardId' ? '' : 0;
            return this.getArrayItemValue(this.rewardSelectedCards, index, field, defaultValue);
        },

        getEventCardValue(index, field) {
            const defaultValue = field === 'id' ? '' : 0;
            return this.getArrayItemValue(this.rewardEventCards, index, field, defaultValue);
        },

        getChestValue(index, field) {
            const defaultValue = field === 'chestId' ? '' : 0;
            return this.getArrayItemValue(this.rewardChests, index, field, defaultValue);
        },

        getShopItemValue(index, field) {
            return this.getArrayItemValue(this.rewardShopItems, index, field, '');
        },
        
        // Generic array management methods
        addArrayItem(arrayKey, countKey, defaultItem) {
            const currentArray = this.safeRewards[arrayKey] || [];
            const newArray = [...currentArray, { ...defaultItem }];
            const currentCount = this.safeRewards[countKey] || 0;
            
            const updatedRewards = {
                ...this.safeRewards,
                [arrayKey]: newArray,
                [countKey]: currentCount + 1
            };
            
            this.emitRewardChange(updatedRewards);
        },
        
        removeArrayItem(arrayKey, countKey, index) {
            const currentArray = this.safeRewards[arrayKey] || [];
            const currentCount = this.safeRewards[countKey] || 0;
            
            if (currentCount > 0 && index < currentArray.length) {
                const newArray = [...currentArray];
                newArray.splice(index, 1);
                
                const updatedRewards = {
                    ...this.safeRewards,
                    [arrayKey]: newArray,
                    [countKey]: Math.max(0, currentCount - 1)
                };
                
                this.emitRewardChange(updatedRewards);
            }
        },
        
        updateArrayItem(arrayKey, index, field, value, defaultItem) {
            // Use safeRewards instead of this.rewards
            const currentArray = this.safeRewards[arrayKey] || [];
            const newArray = [...currentArray];
            
            // Ensure array has enough elements
            while (newArray.length <= index) {
                newArray.push({ ...defaultItem });
            }
            
            // Parse value based on field type
            const parsedValue = this.parseArrayFieldValue(field, value);
            
            // Update the specific field
            newArray[index] = { 
                ...newArray[index], 
                [field]: parsedValue
            };
            
            const updatedRewards = {
                ...this.safeRewards,
                [arrayKey]: newArray
            };
            
            this.emitRewardChange(updatedRewards);
        },
        
        // Helper to parse array field values
        parseArrayFieldValue(field, value) {
            const stringFields = ['packId', 'randomCard', 'cardId', 'id', 'deltaCardId'];
            return stringFields.includes(field) ? value : (parseInt(value) || 0);
        },
        
        // Pack-specific methods
        incrementPack() {
            this.addArrayItem('givePack', 'packs_amount', { packId: '', packs_quantity: 0 });
        },
        
        removePack(index) {
            this.removeArrayItem('givePack', 'packs_amount', index);
        },
        
        updatePack(index, field, value) {
            this.updateArrayItem('givePack', index, field, value, { packId: '', packs_quantity: 0 });
        },
        
        // Random card-specific methods
        incrementRandomCards() {
            this.addArrayItem('randomCard', 'random_amount', { randomCard: '', random_amount: 0 });
        },
        
        removeRandomCard(index) {
            this.removeArrayItem('randomCard', 'random_amount', index);
        },
        
        updateRandomCard(index, field, value) {
            this.updateArrayItem('randomCard', index, field, value, { randomCard: '', random_amount: 0 });
        },
        
        // Selected card-specific methods  
        incrementSelectedCards() {
            this.addArrayItem('selectedCard', 'cards_amount', { cardId: '', card_amount: 0, card_tier: 0 });
        },
        
        removeSelectedCard(index) {
            this.removeArrayItem('selectedCard', 'cards_amount', index);
        },
        
        updateSelectedCard(index, field, value) {

            this.updateArrayItem('selectedCard', index, field, value, { cardId: '', card_amount: 0, card_tier: 0 });
        },

        // Event card-specific methods
        incrementEventCards() {
            this.addArrayItem('eventCard', 'event_cards_amount', { id: '', card_amount: 0, card_tier: 0 });
        },

        removeEventCard(index) {
            this.removeArrayItem('eventCard', 'event_cards_amount', index);
        },

        updateEventCard(index, field, value) {
            this.updateArrayItem('eventCard', index, field, value, { id: '', card_amount: 0, card_tier: 0 });
        },

        // Variant card methods
        getVariantCardValue(index, field) {
            const defaultValue = field === 'cardId' ? '' : 0;
            return this.getArrayItemValue(this.rewardVariantCards, index, field, defaultValue);
        },

        incrementVariantCards() {
            this.addArrayItem('variantCard', 'variant_cards_amount', { cardId: '', card_amount: 0, card_tier: 9 });
        },

        removeVariantCard(index) {
            this.removeArrayItem('variantCard', 'variant_cards_amount', index);
        },

        updateVariantCard(index, field, value) {
            this.updateArrayItem('variantCard', index, field, value, { cardId: '', card_amount: 0, card_tier: 9 });
        },

        // Delta card methods
        getDeltaCardValue(index, field) {
            const defaultValue = field === 'deltaCardId' ? '' : 0;
            return this.getArrayItemValue(this.rewardDeltaCards, index, field, defaultValue);
        },

        incrementDeltaCards() {
            this.addArrayItem('deltaCard', 'delta_cards_amount', { deltaCardId: '', card_amount: 0 });
        },

        removeDeltaCard(index) {
            this.removeArrayItem('deltaCard', 'delta_cards_amount', index);
        },

        updateDeltaCard(index, field, value) {
            this.updateArrayItem('deltaCard', index, field, value, { deltaCardId: '', card_amount: 0 });
        },

        // Exotic card prize methods (1-of-a-kind)
        getExoticCardValue(index, field) {
            return this.getArrayItemValue(this.rewardExoticCards, index, field, '');
        },

        updateExoticCard(index, field, value) {
            this.updateArrayItem('exoticCard', index, field, value, { cardId: '' });
        },

        // Chest-specific methods
        incrementChest() {
            this.addArrayItem('giveChest', 'chests_amount', { chestId: '', chest_quantity: 0 });
        },

        removeChest(index) {
            this.removeArrayItem('giveChest', 'chests_amount', index);
        },

        updateChest(index, field, value) {
            this.updateArrayItem('giveChest', index, field, value, { chestId: '', chest_quantity: 0 });
        },

        // Shop item-specific methods
        incrementShopItem() {
            this.addArrayItem('giveShopItem', 'shop_items_amount', { itemId: '', itemType: '' });
        },

        removeShopItem(index) {
            this.removeArrayItem('giveShopItem', 'shop_items_amount', index);
        },

        updateShopItem(index, field, value) {
            this.updateArrayItem('giveShopItem', index, field, value, { itemId: '', itemType: '' });
        },

        // Helper to update shop item with its type
        updateShopItemSelection(index, itemId) {
            const item = this.ownedShopItems.find(i => i.id === itemId);
            const itemType = item ? item.type : '';

            const currentArray = this.safeRewards.giveShopItem || [];
            const newArray = [...currentArray];

            while (newArray.length <= index) {
                newArray.push({ itemId: '', itemType: '' });
            }

            newArray[index] = { itemId: itemId, itemType: itemType };

            const updatedRewards = {
                ...this.safeRewards,
                giveShopItem: newArray
            };

            this.emitRewardChange(updatedRewards);
        },

        // Reward Frames/Backgrounds methods
        updateRewardFrameMode(mode) {
            const updatedRewards = {
                ...this.safeRewards,
                rewardFrameMode: mode,
                rewardFrames: [] // Clear selection when mode changes
            };
            this.emitRewardChange(updatedRewards);
        },

        updateRewardFramesSelection(selectedIds) {
            // Convert selected IDs to array of {id, type} objects
            const selectedItems = selectedIds.map(itemId => {
                // Check if it's a frame or background
                if (this.frames[itemId]) {
                    return { id: itemId, type: 'frame' };
                } else if (this.backgrounds[itemId]) {
                    return { id: itemId, type: 'background' };
                }
                return null;
            }).filter(Boolean);

            const updatedRewards = {
                ...this.safeRewards,
                rewardFrames: selectedItems
            };
            this.emitRewardChange(updatedRewards);
        },

        // Helper to get item image URL
        getRewardItemImage(item) {
            if (!item) return '';

            if (item.type === 'background') {
                return item.extension
                    ? `${root}/images/backgrounds/${item.id}/${item.id}.${item.extension}?t=${Date.now()}`
                    : '';
            } else {
                return item.uploaded && item.extension
                    ? `${root}/images/frames/${item.id}/${item.id}.${item.extension}?t=${Date.now()}`
                    : '';
            }
        },

        // Helper to get item name
        getRewardItemName(item) {
            return item ? item.name : '';
        },

        // Card back methods
        updateCardBacksSelection(selectedIds) {
            const updatedRewards = {
                ...this.safeRewards,
                rewardCardBacks: selectedIds.filter(id => this.cardBacks[id])
            };
            this.emitRewardChange(updatedRewards);
        },

        removeCardBackItem(id) {
            const updatedRewards = {
                ...this.safeRewards,
                rewardCardBacks: this.selectedCardBacks.filter(existing => existing !== id)
            };
            this.emitRewardChange(updatedRewards);
        },

        // Helper to remove a specific item from selection
        removeRewardItem(itemId) {
            const updatedItems = this.selectedRewardItems.filter(item => item.id !== itemId);
            const updatedRewards = {
                ...this.safeRewards,
                rewardFrames: updatedItems
            };
            this.emitRewardChange(updatedRewards);
        },

        // Utility methods for validation
        isValidRewardType(type) {
            const validTypes = ['barks', 'xp', 'givePack', 'randomCard', 'selectedCard'];
            return validTypes.includes(type);
        },
        
        hasRewardValue(type) {
            if (!this.isValidRewardType(type)) return false;
            
            const value = this.safeRewards[type];
            if (Array.isArray(value)) {
                return value.length > 0 && value.some(item => Object.values(item).some(v => v !== '' && v !== 0));
            }
            return value !== undefined && value !== 0 && value !== '';
        },
        
        // Get total reward count for display
        getTotalRewardCount() {
            let count = 0;
            
            if (this.hasBarks) count++;
            if (this.hasXp) count++;
            if (this.hasGivePack && this.rewardPacks.length > 0) count++;
            if (this.hasRandomCard && this.rewardRandomCards.length > 0) count++;
            if (this.hasSelectedCard && this.rewardSelectedCards.length > 0) count++;
            if (this.hasEventCard && this.rewardEventCards.length > 0) count++;
            
            return count;
        },
    }
});